/* =================================================================
   ALEVIA — CORE JS
   Cart drawer + carrito AJAX + nav mobile + quiz + picker de variantes

   Tres reglas que gobiernan este archivo:
   1) ES5 puro. Sin arrow functions, sin const/let, sin template
      literals, sin async/await. El comprador puede traer cualquier
      navegador.
   2) Toda inicializacion es idempotente y se re-corre en
      'shopify:section:load', scopeada al nodo que Shopify reemplazo.
      Sin esto, el carrito muere apenas el merchant mueve una seccion
      en el Theme Editor.
   3) Ningun fetch asume exito: siempre se chequea response.ok antes
      de tratar la respuesta como valida.
   ================================================================= */

(function () {
  'use strict';

  /* ==========================================================
     POLYFILLS MINIMOS
     ========================================================== */

  if (!Element.prototype.closest) {
    Element.prototype.closest = function (sel) {
      var el = this;
      while (el && el.nodeType === 1) {
        if ((el.matches || el.msMatchesSelector).call(el, sel)) return el;
        el = el.parentElement;
      }
      return null;
    };
  }

  function emit(name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { detail: detail, bubbles: true });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    document.dispatchEvent(ev);
  }

  function each(list, fn) {
    if (!list) return;
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  function qsa(root, sel) {
    return (root || document).querySelectorAll(sel);
  }

  /* Guard de idempotencia: devuelve false si ya se inicializo. */
  function mark(el, flag) {
    if (!el) return false;
    if (el.getAttribute(flag) === '1') return false;
    el.setAttribute(flag, '1');
    return true;
  }

  var hasFetch = typeof window.fetch === 'function';

  /* ==========================================================
     CARRITO — API
     ========================================================== */

  function jsonOrThrow(res) {
    /* fetch() solo rechaza en errores de RED. Un 404/422 llega como
       promesa resuelta, asi que hay que mirar response.ok a mano. */
    if (!res.ok) {
      return res.json().then(
        function (body) {
          throw new Error((body && (body.description || body.message)) || 'Error del carrito');
        },
        function () {
          throw new Error('Error del carrito (' + res.status + ')');
        }
      );
    }
    return res.json();
  }

  function fetchCart() {
    if (!hasFetch) return Promise.reject(new Error('sin fetch'));
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(jsonOrThrow);
  }

  function addToCart(variantId, quantity) {
    if (!hasFetch) return Promise.reject(new Error('sin fetch'));
    if (!variantId) return Promise.reject(new Error('Falta la variante'));
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }] })
    }).then(jsonOrThrow);
  }

  function changeCart(line, quantity) {
    if (!hasFetch) return Promise.reject(new Error('sin fetch'));
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(jsonOrThrow);
  }

  function formatARS(cents) {
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency', currency: 'ARS',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return '$' + Math.round(cents / 100);
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ==========================================================
     CART DRAWER
     ========================================================== */

  function getDrawer() { return document.querySelector('[data-cart-drawer]'); }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    var overlay = document.querySelector('[data-cart-drawer-overlay]');
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    startUrgency();
    var closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 300);
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    var overlay = document.querySelector('[data-cart-drawer-overlay]');
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
    stopUrgency();
  }

  function updateCartCount(count) {
    each(qsa(document, '[data-cart-count]'), function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* Barra de envio gratis: se calcula CONTRA EL SUBTOTAL REAL y el
     umbral configurable del drawer. Antes estaba clavada al 100%. */
  function updateShippingBar(cart) {
    var drawer = getDrawer();
    if (!drawer) return;
    var wrap = drawer.querySelector('[data-cart-shipping]');
    var fill = drawer.querySelector('[data-shipping-fill]');
    var text = drawer.querySelector('[data-shipping-text] span');
    if (!wrap) return;

    var threshold = parseInt(wrap.getAttribute('data-threshold') || '0', 10);
    var msgUnlocked = wrap.getAttribute('data-msg-unlocked') || 'Envio gratis incluido';
    var msgRemaining = wrap.getAttribute('data-msg-remaining') || 'Te faltan [monto] para el envio gratis';

    if (!threshold || threshold <= 0) {
      if (fill) fill.style.width = '100%';
      if (text) text.textContent = msgUnlocked;
      return;
    }

    var subtotal = cart.total_price || 0;
    var pct = Math.min(100, Math.round((subtotal / threshold) * 100));
    if (fill) fill.style.width = pct + '%';
    if (text) {
      text.textContent = subtotal >= threshold
        ? msgUnlocked
        : msgRemaining.replace('[monto]', formatARS(threshold - subtotal));
    }
  }

  /* Urgencia por corte de despacho.
     El reloj cuenta hasta una hora REAL que fija el merchant, y pasada
     esa hora el mensaje cambia solo al del dia siguiente. No es un
     temporizador de carrito: el carrito no se reserva y fingir que si
     es exactamente lo que este comprador detecta. */
  var urgencyTimer = null;

  function renderUrgency() {
    var drawer = getDrawer();
    if (!drawer) return;
    var box = drawer.querySelector('[data-cart-urgency]');
    if (!box || box.getAttribute('data-mode') !== 'cutoff') return;

    var out = box.querySelector('[data-urgency-text]');
    if (!out) return;

    var hour = parseInt(box.getAttribute('data-cutoff-hour') || '15', 10);
    var before = box.getAttribute('data-msg-before') || '';
    var after = box.getAttribute('data-msg-after') || '';

    var now = new Date();
    var cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, 0, 0, 0);
    var ms = cutoff.getTime() - now.getTime();

    if (ms <= 0) { out.textContent = after; return; }

    var totalMin = Math.floor(ms / 60000);
    var h = Math.floor(totalMin / 60);
    var m = totalMin % 60;
    var label = h > 0 ? (h + ' h ' + m + ' min') : (m + ' min');

    /* [tiempo] es el marcador editable del setting */
    if (before.indexOf('[tiempo]') !== -1) {
      var parts = before.split('[tiempo]');
      out.innerHTML = '';
      out.appendChild(document.createTextNode(parts[0]));
      var strong = document.createElement('strong');
      strong.textContent = label;
      out.appendChild(strong);
      if (parts[1]) out.appendChild(document.createTextNode(parts[1]));
    } else {
      out.textContent = before;
    }
  }

  function startUrgency() {
    renderUrgency();
    if (urgencyTimer) clearInterval(urgencyTimer);
    urgencyTimer = setInterval(renderUrgency, 30000);
  }

  function stopUrgency() {
    if (urgencyTimer) { clearInterval(urgencyTimer); urgencyTimer = null; }
  }

  function updateSavings(cart) {
    var drawer = getDrawer();
    if (!drawer) return;
    var row = drawer.querySelector('[data-cart-savings]');
    var amount = drawer.querySelector('[data-cart-savings-amount]');
    if (!row) return;
    var total = 0;
    each(cart.items, function (item) {
      if (item.original_line_price && item.final_line_price) {
        total += (item.original_line_price - item.final_line_price);
      }
    });
    row.style.display = total > 0 ? 'flex' : 'none';
    if (total > 0 && amount) amount.textContent = formatARS(total);
  }

  function updateInstallments(cart) {
    var drawer = getDrawer();
    if (!drawer) return;
    var row = drawer.querySelector('[data-cart-installments]');
    var amount = drawer.querySelector('[data-cart-installment-amount]');
    if (!row) return;
    row.style.display = cart.total_price > 0 ? 'block' : 'none';
    if (cart.total_price > 0 && amount) amount.textContent = formatARS(Math.round(cart.total_price / 3));
  }

  /* Upsell del drawer: carga el producto sugerido por handle. */
  function updateUpsell(cart) {
    var drawer = getDrawer();
    if (!drawer || !hasFetch) return;
    var slot = drawer.querySelector('[data-cart-upsell]');
    if (!slot) return;
    var handle = slot.getAttribute('data-upsell-handle');
    if (!handle || slot.getAttribute('data-loaded') === '1') return;

    /* Si el producto ya esta en el carrito, no lo ofrecemos. */
    var already = false;
    each(cart.items, function (item) { if (item.handle === handle) already = true; });
    if (already) { slot.style.display = 'none'; return; }

    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(jsonOrThrow)
      .then(function (product) {
        var v = product.variants && product.variants[0];
        if (!v || !v.available) { slot.style.display = 'none'; return; }
        slot.setAttribute('data-loaded', '1');
        slot.style.display = '';
        var img = slot.querySelector('[data-upsell-image]');
        var title = slot.querySelector('[data-upsell-title]');
        var price = slot.querySelector('[data-upsell-price]');
        var btn = slot.querySelector('[data-upsell-add]');
        if (img && product.featured_image) {
          img.setAttribute('src', product.featured_image.replace(/(\.[^.]+)(\?|$)/, '_120x$1$2'));
          img.setAttribute('alt', product.title);
        }
        if (title) title.textContent = product.title;
        if (price) price.textContent = formatARS(v.price);
        if (btn) btn.setAttribute('data-variant-id', v.id);
      })
      .catch(function () { slot.style.display = 'none'; });
  }

  function renderEmpty(drawer) {
    var cfg = drawer.querySelector('[data-empty-config]');
    var title = (cfg && cfg.getAttribute('data-empty-title')) || 'Todavia no agregaste nada';
    var sub = (cfg && cfg.getAttribute('data-empty-sub')) || '';
    var cta = (cfg && cfg.getAttribute('data-empty-cta')) || 'Ver el producto';
    var url = (cfg && cfg.getAttribute('data-empty-url')) || '/collections/all';
    return '' +
      '<div class="cart-drawer__empty">' +
        '<div class="cart-drawer__empty-icon" aria-hidden="true">' +
          '<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>' +
            '<path d="M3 6h18"/>' +
            '<path d="M16 10a4 4 0 0 1-8 0"/>' +
          '</svg>' +
        '</div>' +
        '<p class="cart-drawer__empty-title">' + esc(title) + '</p>' +
        '<p class="cart-drawer__empty-sub">' + esc(sub) + '</p>' +
        /* Sin data-cart-close: es un link que tiene que NAVEGAR.
           El handler global hace preventDefault sobre [data-cart-close]. */
        '<a href="' + esc(url) + '" class="btn btn--primary" style="margin-top:1rem;">' + esc(cta) + '</a>' +
      '</div>';
  }

  function renderItems(cart) {
    var html = '';
    each(cart.items, function (item, idx) {
      var line = idx + 1;
      var hasDiscount = item.original_line_price > item.final_line_price;
      html += '' +
        '<div class="cart-item" data-line="' + line + '">' +
          '<div class="cart-item__img">' +
            (item.image ? '<img src="' + esc(item.image.replace(/(\.[^.]+)(\?|$)/, '_120x$1$2')) + '" alt="' + esc(item.title) + '" loading="lazy" width="60" height="60">' : '') +
          '</div>' +
          '<div class="cart-item__info">' +
            '<p class="cart-item__name">' + esc(item.product_title) + '</p>' +
            (item.variant_title && item.variant_title !== 'Default Title'
              ? '<p class="cart-item__variant">' + esc(item.variant_title) + '</p>' : '') +
            '<div class="cart-item__qty">' +
              '<button type="button" data-cart-decrease="' + line + '" aria-label="Restar uno">-</button>' +
              '<span aria-live="polite">' + item.quantity + '</span>' +
              '<button type="button" data-cart-increase="' + line + '" aria-label="Sumar uno">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="cart-item__price-wrap">' +
            '<span class="cart-item__price">' + formatARS(item.final_line_price) + '</span>' +
            (hasDiscount ? '<span class="cart-item__price-old">' + formatARS(item.original_line_price) + '</span>' : '') +
            '<button type="button" class="cart-item__remove" data-cart-remove="' + line + '" aria-label="Quitar">Quitar</button>' +
          '</div>' +
        '</div>';
    });
    return html;
  }

  function refreshDrawer() {
    var drawer = getDrawer();
    if (!drawer) return Promise.resolve();
    return fetchCart().then(function (cart) {
      updateCartCount(cart.item_count);
      updateShippingBar(cart);
      updateSavings(cart);
      updateInstallments(cart);
      updateUpsell(cart);

      var body = drawer.querySelector('[data-cart-body]');
      var foot = drawer.querySelector('[data-cart-foot]');
      if (!body) return;

      if (cart.item_count === 0) {
        body.innerHTML = renderEmpty(drawer);
        if (foot) foot.style.display = 'none';
        return;
      }

      if (foot) foot.style.display = '';
      body.innerHTML = renderItems(cart);
      var subtotalEl = drawer.querySelector('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatARS(cart.total_price);
    }).catch(function (err) {
      if (window.console) console.warn('[alevia] carrito:', err.message);
    });
  }

  /* ---- listeners globales del carrito (se atan una sola vez) ---- */

  function bindCartGlobals() {
    if (!mark(document.documentElement, 'data-alevia-cart-bound')) return;

    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-cart-toggle], [data-cart-open]');
      if (toggle) {
        e.preventDefault();
        openDrawer();
        refreshDrawer();
        return;
      }
      /* Solo botones cierran; un <a> con data-cart-close podria querer
         navegar, asi que no le robamos el click. */
      var closeEl = e.target.closest('[data-cart-close]');
      if (closeEl) {
        if (closeEl.tagName !== 'A') e.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', function (e) {
      var drawer = getDrawer();
      if ((e.key === 'Escape' || e.keyCode === 27) && drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    /* Cantidad y quitar, delegado en document para sobrevivir al
       re-render del drawer. */
    document.addEventListener('click', function (e) {
      var rem = e.target.closest('[data-cart-remove]');
      var inc = e.target.closest('[data-cart-increase]');
      var dec = e.target.closest('[data-cart-decrease]');
      if (!rem && !inc && !dec) return;
      e.preventDefault();

      if (rem) {
        changeCart(parseInt(rem.getAttribute('data-cart-remove'), 10), 0)
          .then(function (cart) { emit('cart:updated', cart); refreshDrawer(); })
          .catch(function (err) { if (window.console) console.warn('[alevia]', err.message); });
        return;
      }

      var el = inc || dec;
      var lineIdx = parseInt(el.getAttribute(inc ? 'data-cart-increase' : 'data-cart-decrease'), 10);
      fetchCart().then(function (cart) {
        var line = cart.items[lineIdx - 1];
        if (!line) return;
        var qty = inc ? line.quantity + 1 : Math.max(0, line.quantity - 1);
        return changeCart(lineIdx, qty).then(function (updated) {
          emit('cart:updated', updated);
          refreshDrawer();
        });
      }).catch(function (err) { if (window.console) console.warn('[alevia]', err.message); });
    });

    /* Add to cart delegado: cualquier boton con data-add-to-cart o
       data-upsell-add. Si no hay fetch, el click cae al form nativo. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart], [data-upsell-add], [data-whisp-tier-add]');
      if (!btn || !hasFetch) return;
      e.preventDefault();
      if (btn.hasAttribute('aria-busy')) return;

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      btn.setAttribute('aria-busy', 'true');
      var span = btn.querySelector('.add-to-cart-text, .whisp-tier-card__cta-text');
      var original = span ? span.textContent : null;
      if (span) span.textContent = 'Agregando...';

      addToCart(variantId, parseInt(btn.getAttribute('data-quantity') || '1', 10))
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          emit('cart:updated', cart);
          openDrawer();
          return refreshDrawer();
        })
        .catch(function (err) {
          if (window.console) console.warn('[alevia]', err.message);
          var live = document.querySelector('[data-cart-error]');
          if (live) live.textContent = err.message;
        })
        .then(function () {
          btn.removeAttribute('aria-busy');
          if (span && original !== null) span.textContent = original;
        });
    });

    document.addEventListener('cart:updated', function (e) {
      if (e.detail) updateCartCount(e.detail.item_count);
    });

    fetchCart().then(function (c) { updateCartCount(c.item_count); }).catch(function () {});
  }

  /* ==========================================================
     NAV MOBILE
     ========================================================== */

  function initNav(root) {
    each(qsa(root, '[data-nav-toggle]'), function (btn) {
      if (!mark(btn, 'data-alevia-bound')) return;
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-nav-toggle');
        var panel = document.getElementById(id);
        if (!panel) return;
        var open = panel.classList.toggle('is-open');
        /* aria se actualiza en el MISMO lugar donde se togglea la clase */
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.setAttribute('aria-hidden', open ? 'false' : 'true');
        document.body.style.overflow = open ? 'hidden' : '';
      });
    });

    if (mark(document.documentElement, 'data-alevia-nav-esc')) {
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' && e.keyCode !== 27) return;
        each(qsa(document, '.header__mobile-panel.is-open'), function (panel) {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
          var btn = document.querySelector('[data-nav-toggle="' + panel.id + '"]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      });
    }
  }

  /* ==========================================================
     FAQ — cerrar los demas al abrir uno
     ========================================================== */

  function initFaq(root) {
    each(qsa(root, '.faq-item'), function (item) {
      if (!mark(item, 'data-alevia-bound')) return;
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        each(qsa(document, '.faq-item[open]'), function (other) {
          if (other !== item) other.removeAttribute('open');
        });
      });
    });
  }

  /* ==========================================================
     PICKER DE VARIANTES
     ========================================================== */

  function initVariantPicker(root) {
    each(qsa(root, '[data-variant-picker]'), function (picker) {
      if (!mark(picker, 'data-alevia-bound')) return;
      picker.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-variant]');
        if (!btn) return;
        each(qsa(picker, '[data-variant]'), function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');

        var variantId = btn.getAttribute('data-variant');
        var price = btn.getAttribute('data-price');
        var priceOld = btn.getAttribute('data-price-old');
        var priceEl = document.querySelector('[data-product-price]');
        var priceOldEl = document.querySelector('[data-product-price-old]');
        var addBtn = document.querySelector('[data-add-to-cart]');
        if (priceEl && price) priceEl.textContent = price;
        if (priceOldEl && priceOld) priceOldEl.textContent = priceOld;
        if (addBtn && variantId) addBtn.setAttribute('data-variant-id', variantId);

        /* Las secciones independientes (sticky ATC) se enteran por
           evento, nunca acoplandose a este DOM. */
        emit('variant:changed', {
          variantId: variantId,
          price: price,
          priceOld: priceOld,
          label: btn.getAttribute('data-variant-label') || ''
        });
      });
    });
  }

  /* ==========================================================
     QUIZ
     ========================================================== */

  function initQuiz(root) {
    each(qsa(root, '[data-quiz]'), function (quiz) {
      if (!mark(quiz, 'data-alevia-bound')) return;
      var answers = {};
      var steps = qsa(quiz, '[data-quiz-step]');
      var totalSteps = 0;
      each(steps, function (s) {
        if (s.getAttribute('data-quiz-step') !== 'result') totalSteps++;
      });

      function show(key) {
        each(steps, function (s) {
          var active = s.getAttribute('data-quiz-step') === String(key);
          if (active) s.classList.add('is-active'); else s.classList.remove('is-active');
          s.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }

      quiz.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-quiz-option]');
        if (opt) {
          var step = opt.getAttribute('data-step');
          answers[step] = parseInt(opt.getAttribute('data-value'), 10) || 0;
          var next = parseInt(step, 10) + 1;
          var bar = quiz.querySelector('[data-quiz-progress]');

          if (next <= totalSteps) {
            show(next);
            if (bar) bar.style.width = Math.round((next / totalSteps) * 100) + '%';
          } else {
            show('result');
            if (bar) bar.style.width = '100%';
            var total = 0;
            for (var k in answers) { if (answers.hasOwnProperty(k)) total += answers[k]; }
            /* Los textos del resultado son settings de la seccion, no
               strings de este archivo. */
            var out = quiz.querySelector('[data-quiz-result-plan]');
            var hi = quiz.getAttribute('data-result-high');
            var mid = quiz.getAttribute('data-result-mid');
            var lo = quiz.getAttribute('data-result-low');
            if (out) out.textContent = total >= 7 ? (hi || '') : (total >= 4 ? (mid || '') : (lo || ''));
          }
          return;
        }
        if (e.target.closest('[data-quiz-restart]')) {
          answers = {};
          show(1);
          var pb = quiz.querySelector('[data-quiz-progress]');
          if (pb) pb.style.width = Math.round((1 / totalSteps) * 100) + '%';
        }
      });

      show(1);
    });
  }

  /* ==========================================================
     BOOT — carga inicial + ciclo de vida del Theme Editor
     ========================================================== */

  function initScope(root) {
    initNav(root);
    initFaq(root);
    initVariantPicker(root);
    initQuiz(root);
  }

  function boot() {
    bindCartGlobals();
    initScope(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify reemplaza el nodo de la seccion sin redisparar
     DOMContentLoaded. Sin esto, toda la interactividad queda muerta
     hasta recargar la pagina. */
  document.addEventListener('shopify:section:load', function (e) {
    initScope(e.target || document);
    if (e.target && e.target.querySelector('[data-cart-drawer]')) refreshDrawer();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector('[data-cart-drawer]')) closeDrawer();
  });

  /* API publica minima para las secciones */
  window.AleviaCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
    add: addToCart,
    fetch: fetchCart,
    format: formatARS
  };
})();
